import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Send, FileText, CheckCircle2, Download } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

export default function PresentationCTA() {
  const { lang } = useLang();

  const content = lang === 'ru' ? {
    title: 'Получите презентацию франшизы KidsMafia',
    subtitle: 'Отправим подробную презентацию с цифрами, форматами и моделью запуска.',
    bullets: [
      'форматы франшизы',
      'инвестиционная модель',
      'структура доходов',
      'условия входа',
      'сценарии запуска',
    ],
    supporting: 'Запросите презентацию напрямую — без длинных форм и ожидания.',
    cta_telegram: 'Написать в Telegram',
    cta_phone: 'Позвонить',
    pdf_title: 'KIDSMАFIA',
    pdf_subtitle: 'Презентация франшизы',
    pdf_pages: '24 страницы',
  } : {
    title: 'Obtenga la presentación de la franquicia KidsMafia',
    subtitle: 'Enviaremos una presentación detallada con cifras, formatos y modelo de lanzamiento.',
    bullets: [
      'formatos de franquicia',
      'modelo de inversión',
      'estructura de ingresos',
      'condiciones de entrada',
      'escenarios de lanzamiento',
    ],
    supporting: 'Solicite la presentación directamente — sin formularios largos ni esperas.',
    cta_telegram: 'Escribir por Telegram',
    cta_phone: 'Llamar',
    pdf_title: 'KIDSMАFIA',
    pdf_subtitle: 'Presentación de franquicia',
    pdf_pages: '24 páginas',
  };

  return (
    <section className="relative py-20 md:py-28 px-6 overflow-hidden">
      {/* Background */}
      <div
        className="absolute inset-0"
        style={{
          background: 'linear-gradient(135deg, rgba(212,175,55,0.08) 0%, rgba(10,11,15,1) 100%)',
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Title */}
            <h2 className="text-3xl md:text-5xl font-black font-heading gold-text mb-6 leading-tight">
              {content.title}
            </h2>

            {/* Subtitle */}
            <p className="text-white text-lg md:text-xl mb-8 leading-relaxed">
              {content.subtitle}
            </p>

            {/* Bullets */}
            <div className="mb-8 space-y-3">
              {content.bullets.map((bullet, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <CheckCircle2 size={20} className="text-[#22C55E] flex-shrink-0" />
                  <span className="text-white/90 text-base md:text-lg">
                    {bullet}
                  </span>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <motion.a
                href="https://t.me/SerjValencia"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="btn-telegram flex items-center justify-center gap-3 px-8 py-4 rounded-full text-base font-bold shadow-2xl"
              >
                <Send size={20} />
                {content.cta_telegram}
              </motion.a>
              <motion.a
                href="tel:+34624586917"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="btn-call flex items-center justify-center gap-3 px-8 py-4 rounded-full text-base font-bold shadow-2xl"
              >
                <Phone size={20} />
                {content.cta_phone}
              </motion.a>
            </div>

            {/* Supporting line */}
            <p className="text-white/60 text-sm italic">
              {content.supporting}
            </p>
          </motion.div>

          {/* Right: PDF Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative mx-auto max-w-sm">
              {/* PDF Document Card */}
              <motion.div
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                className="relative"
              >
                {/* Main PDF Card */}
                <div
                  className="relative rounded-2xl overflow-hidden border-2 border-white/10"
                  style={{
                    boxShadow: '0 30px 80px rgba(212,175,55,0.3), 0 0 0 1px rgba(212,175,55,0.2)',
                    background: 'linear-gradient(135deg, #1A1B1F 0%, #0A0B0F 100%)',
                  }}
                >
                  {/* PDF Header */}
                  <div className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] p-6 text-center">
                    <FileText size={48} className="mx-auto mb-3 text-[#0A0B0F]" />
                    <h3 className="text-2xl font-black text-[#0A0B0F] mb-1">
                      {content.pdf_title}
                    </h3>
                    <p className="text-sm font-semibold text-[#0A0B0F]/70">
                      {content.pdf_subtitle}
                    </p>
                  </div>

                  {/* PDF Body - Simulated Pages */}
                  <div className="p-6 space-y-3">
                    {[1, 2, 3, 4, 5].map((line) => (
                      <div key={line} className="space-y-2">
                        <div className="h-2 bg-white/10 rounded-full w-full" />
                        <div className="h-2 bg-white/5 rounded-full w-4/5" />
                      </div>
                    ))}
                  </div>

                  {/* PDF Footer */}
                  <div className="border-t border-white/10 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Download size={16} className="text-[#D4AF37]" />
                      <span className="text-white/70 text-sm font-semibold">
                        PDF
                      </span>
                    </div>
                    <span className="text-white/50 text-xs">
                      {content.pdf_pages}
                    </span>
                  </div>
                </div>

                {/* Glow effect */}
                <div
                  className="absolute inset-0 -z-10 blur-3xl opacity-50"
                  style={{
                    background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)',
                  }}
                />
              </motion.div>

              {/* Decorative stacked effect */}
              <div
                className="absolute -bottom-3 left-6 right-6 h-8 rounded-b-2xl border-x-2 border-b-2 border-white/5 -z-10"
                style={{
                  background: 'linear-gradient(135deg, #1A1B1F 0%, #0A0B0F 100%)',
                }}
              />
              <div
                className="absolute -bottom-6 left-12 right-12 h-8 rounded-b-2xl border-x-2 border-b-2 border-white/5 -z-20"
                style={{
                  background: 'linear-gradient(135deg, #1A1B1F 0%, #0A0B0F 100%)',
                }}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
